import { Component, effect, OnInit, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { SettingsService } from 'src/app/shared/services/settings';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as _ from 'lodash';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';

// Declare Helpers globally
declare const Helpers: any;

@Component({
  selector: 'app-litmus-action-inbox',
  templateUrl: './litmus-action-inbox.component.html',
  styleUrls: ['./litmus-action-inbox.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class LitmusActionInboxComponent implements OnInit, OnChanges, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);
  configuration: any = {};
  inboxForm!: FormGroup;
  isSaving = false;
  isCancelling = false;
  isSaveEnabled = false;
  originalConfiguration: any = {};
  originalAvailableFields: any[] = [];
  brandDetails: any = {};
  selectedInboxList: any[] = [];
  configurationLoaded: boolean = false;
  availableFields: any[] = [];
  fieldDefinitionsLoaded: boolean = false;
  previousSlaStatus: boolean = false;
  previousTatStatus: boolean = false;
  originalFieldDefinitions: any[] = [];
  restrictedFields: any[] = ["shop_name", "created_date", "primary_rating", "comments", "feed_status", "notes", "name", "tag_feedback_reference_token", "tags", "rated_medium", "responded_using"];
  constructor(
    private fb: FormBuilder,
    private _toast: ToastService,
    private stateService: AppStateService,
    private _settingsService: SettingsService,
    private accountsService: AccountsService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        const brandDetail: any = this.stateService.brandDetails();
        if (!_.isEmpty(brandDetail)) {
          this.brandDetails = brandDetail;
          this.configurationLoaded = true;
          // Store previous status values for change detection with null checks
          this.previousSlaStatus = brandDetail?.is_age_sla_timer_enabled || false;
          this.previousTatStatus = brandDetail?.is_new_tat_filter_enabled || false;
          // Update form values when brand details are available
          this.updateFormWithBrandDetails();
          // Call getFieldDefinitions service method when brand details are available
          this.loadFieldDefinitions();
        }
      }
    })
  }

  ngOnInit(): void {
    // Initialize original configuration
    this.originalConfiguration = { ...this.configuration };
    this.originalAvailableFields = JSON.parse(JSON.stringify(this.availableFields));

    // Initialize form immediately to prevent template errors
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly']) {
      this.updateFormReadonlyState();
    }
  }

  private updateFormReadonlyState(): void {
    if (!this.inboxForm) {
      return;
    }
    if (this.readonly) {
      this.inboxForm.disable({ emitEvent: false });
    } else {
      this.inboxForm.enable({ emitEvent: false });
    }
  }

  /**
   * Update form values when brand details are available
   */
  private updateFormWithBrandDetails(): void {
    if (this.inboxForm && this.brandDetails) {
      this.inboxForm.patchValue({
        activeInboxTATFilter: this.brandDetails?.is_new_tat_filter_enabled || false,
        activateSLATimerFunction: this.brandDetails?.is_age_sla_timer_enabled || false,
        customFields: this.getSelectedCustomFields()
      });
    }
  }

  /**
   * Load field definitions from settings service
   */
  private loadFieldDefinitions(): void {
    if (!this.brandDetails?._id) {
      console.warn('BrandId not available for loading field definitions');
      return;
    }

    if (this.fieldDefinitionsLoaded) {
      return;
    }

    const params = {
      'brand_id': this.brandDetails?._id,
      'source': 'inbox_detail',
      'type': 1
    };


    this._settingsService.getFieldDefinitions(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.code === 0 && response.data && response.data.field_definitions) {
            // Process the field definitions and update availableFields
            this.processFieldDefinitions(response.data.field_definitions);
            this.fieldDefinitionsLoaded = true;
          }
        },
        error: (error: any) => {
          console.error('Error loading field definitions:', error);
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Error while loading field definitions', 'error', 3000);
          }
        }
      });
  }

  /**
   * Process field definitions and update availableFields
   */
  private processFieldDefinitions(fieldDefinitions: any[]): void {
    // Store original field definitions for change detection
    this.originalFieldDefinitions = JSON.parse(JSON.stringify(fieldDefinitions));

    // Transform field definitions to match the availableFields structure
    let processedFields: any = []

    _.each(fieldDefinitions, (field: any) => {
      if (!this.restrictedFields.includes(field.field_id)) {
        //Types of information which can be displayed in the inbox: Transaction data, Category, Subcategory, Extensible fields of type list and date, Assignee
        if ((field && !field.custom_parameter_type) || field.custom_parameter_type == 4 && (field.data_type == "list" || field.data_type == "date" || field.data_type == "string")) {
          processedFields.push({
            key: field.field_id || field.id,
            label: field.name || field.display_name || field.field_id,
            selected: field.show_in_inbox || false
          })
        }
      }
    });

    // Update availableFields with processed data
    this.availableFields = JSON.parse(JSON.stringify(processedFields));
    this.originalAvailableFields = JSON.parse(JSON.stringify(this.availableFields));
    this.selectedInboxList = this.getSelectedCustomFields();
  }

  private initializeForm(): void {
    this.selectedInboxList = this.getSelectedCustomFields()
    this.inboxForm = this.fb.group({
      // Inbox TAT Filter
      activeInboxTATFilter: [this.configuration?.activeInboxTATFilter || false],

      // SLA Timer
      activateSLATimerFunction: [this.configuration?.activateSLATimerFunction || false],

      // Custom fields (will be populated from availableFields)
      customFields: [...this.selectedInboxList]
    });

    // Listen to form changes with debounce and cleanup
    this.inboxForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.onFormChange(value);
      });

    this.updateFormReadonlyState();
  }

  /**
   * Refresh field definitions (can be called externally if needed)
   */
  public refreshFieldDefinitions(): void {
    this.fieldDefinitionsLoaded = false;
    this.loadFieldDefinitions();
  }

  private getSelectedCustomFields(): string[] {
    return this.availableFields
      .filter(field => field.selected)
      .map(field => field.key);
  }

  onFormChange(value: any): void {
    const updatedConfig = {
      ...this.configuration,
      ...value
    };
    this.isSaveEnabled = this.hasFormChanged();
  }

  onCheckboxChange(field: string, value: boolean): void {
    const updatedConfig = {
      ...this.configuration,
      [field]: value
    };
    this.isSaveEnabled = this.hasFormChanged();
  }

  onCustomFieldToggle(fieldKey: string, selected: boolean): void {
    const field = this.availableFields.find(f => f.key === fieldKey);
    if (field) {
      field.selected = selected;

      const selectedFields = this.getSelectedCustomFields();
      this.selectedInboxList = selectedFields;
      const updatedConfig = {
        ...this.configuration,
        customFields: selectedFields
      };
      this.isSaveEnabled = this.hasFormChanged();
    }
  }

  isCustomFieldSelected(fieldKey: string): boolean {
    const field = this.availableFields.find(f => f.key === fieldKey);
    return field ? field.selected : false;
  }

  /**
   * Check if form has changed following reference pattern
   */
  private hasFormChanged(): boolean {
    if (!this.inboxForm || !this.brandDetails) return false;

    const currentValues = this.inboxForm.value;

    // Check SLA status change
    const slaConfigChanged = (currentValues.activateSLATimerFunction !== this.previousSlaStatus);

    // Check TAT filter status change
    const tatFilterChanged = (currentValues.activeInboxTATFilter !== this.previousTatStatus);

    // Check field definitions changes
    const fieldDefinitionsChanged = this.hasFieldDefinitionsChanged();

    return slaConfigChanged || tatFilterChanged || fieldDefinitionsChanged;
  }

  /**
   * Check if field definitions have changed
   */
  private hasFieldDefinitionsChanged(): boolean {
    if (!this.originalFieldDefinitions.length || !this.availableFields.length) {
      return false;
    }

    // Compare current field selections with original field definitions
    for (const field of this.availableFields) {
      if (!this.restrictedFields.includes(field.key)) {
        const originalField = this.originalFieldDefinitions.find(f =>
          (f.field_id || f.id) === field.key
        );
  
        if (originalField && _.has(originalField, 'show_in_inbox') && originalField.show_in_inbox !== field.selected) {
          return true;
        }
      }
      
    }

    return false;
  }

  /**
   * Save the inbox configuration following reference pattern
   */
  saveConfiguration() {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (this.isSaving) return;

    if (!this.brandDetails?._id) {
      console.error('Brand details not available for saving configuration');
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', 'Brand details not available', 'error', 3500);
      }
      return;
    }

    this.isSaving = true;
    const inboxFormConfig = this.inboxForm.value;

    // updatedFieldsList[] Array contain updated field values and used for calling a service for API call
    const updatedFieldsList: any[] = [];

    // flag to check if SLA params was changed
    const slaConfigChanged = (inboxFormConfig.activateSLATimerFunction !== this.previousSlaStatus);
    const tatFilterChanged = (inboxFormConfig.activeInboxTATFilter !== this.previousTatStatus);

    // Save TAT filter configuration if changed
    if (slaConfigChanged || tatFilterChanged) {
      const updatedConfig = {}

      if (slaConfigChanged) {
        updatedConfig["is_age_sla_timer_enabled"] = inboxFormConfig.activateSLATimerFunction
      }
      if (tatFilterChanged) {
        updatedConfig["is_new_tat_filter_enabled"] = inboxFormConfig.activeInboxTATFilter
      }

      this._settingsService.updateBrandDashboardConfiguration(this.brandDetails._id, updatedConfig)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            if (response && response.code == 0) {
              Helpers.smallAlertBox('Success', 'Action inbox configuration has been updated successfully', 'success', 3500);
              this.previousTatStatus = inboxFormConfig.activeInboxTATFilter;
              this.brandDetails.is_new_tat_filter_enabled = inboxFormConfig.activeInboxTATFilter;
              this.previousSlaStatus = inboxFormConfig.activateSLATimerFunction;
              this.brandDetails.is_age_sla_timer_enabled = inboxFormConfig.activateSLATimerFunction;
              this.stateService.updateBrandDetails(this.brandDetails);
            } else {
              console.error('Error while updating Action inbox configuration:', response);
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Error', 'Error while updating Action inbox configuration', 'error', 3500);
              }
            }
          },
          error: (error: any) => {
            console.error('Error updating TAT filter configuration:', error);
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Error', 'Error while updating TAT filter settings', 'error', 3500);
            }
          }
        });
    }

    // Loop for Checking any Update occur or not
    for (const field of this.availableFields) {
      const originalField = this.originalFieldDefinitions.find(f =>
        (f.field_id || f.id) === field.key
      );

      if (originalField && originalField.show_in_inbox !== field.selected) {
        // If Change occurred pushed in Array updatedFieldsList[] with updated field values
        const updatedField = { ...originalField, show_in_inbox: field.selected };
        updatedFieldsList.push(updatedField);
      }
    }

    // Checking any Field Updated if 'true' than calling a service for Updating field definitions
    if (updatedFieldsList.length !== 0 || slaConfigChanged || tatFilterChanged) {
      if (updatedFieldsList.length !== 0) {
        this._settingsService.updateFieldDefinition(this.brandDetails._id, updatedFieldsList)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (response: any) => {
              if (response && response.code == 0) {
                Helpers.smallAlertBox('Success', 'Field definitions has been updated successfully', 'success', 3500);
              } else {
                console.error('Error while updating Field definitions:', response);
                if (Helpers && Helpers.smallAlertBox) {
                  Helpers.smallAlertBox('Error', 'Error while updating Field definitions', 'error', 3500);
                }
              }
              // Reload field definitions after successful update
              this.refreshFieldDefinitions();
            },
            error: (error: any) => {
              console.error('Error while updating Field definitions:', error);
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Error', 'Error while updating Field definitions', 'error', 3500);
              }
              // Reload field definitions after successful update
              this.refreshFieldDefinitions();
            }
          });

      }

      this.isSaveEnabled = false;
    } else {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', 'Nothing to update', 'error', 3500);
      }
    }

    this.isSaving = false;
  }

  /**
   * Get the latest brand details and update it on helpers and app state
   * Update original field definitions and available fields for proper cancel functionality
   */
  afterUpdateBrandConfig() {
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);

        // Update form values after reloading brand details
        this.updateFormWithBrandDetails();

        // Update original field definitions and available fields for proper cancel functionality
       this.refreshFieldDefinitions();
        // Update previous status values to current values
        this.previousSlaStatus = this.brandDetails?.is_age_sla_timer_enabled || false;
        this.previousTatStatus = this.brandDetails?.is_new_tat_filter_enabled || false;
      }
    });
  }

  /**
   * Cancel the inbox configuration changes - reset to original state without API call
   */
  cancelConfiguration() {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (this.isCancelling) return;

    this.isCancelling = true;

    // Reset available fields to original state
    this.availableFields = JSON.parse(JSON.stringify(this.originalAvailableFields));

    // Reset original field definitions to original state
    this.originalFieldDefinitions = JSON.parse(JSON.stringify(this.originalFieldDefinitions));

    this.selectedInboxList = this.getSelectedCustomFields()

    // Reset form to original brand details values
    this.inboxForm.patchValue({
      activeInboxTATFilter: this.brandDetails?.is_new_tat_filter_enabled || false,
      activateSLATimerFunction: this.brandDetails?.is_age_sla_timer_enabled || false,
      customFields: this.selectedInboxList
    });

    // Reset previous status values to current brand details values
    this.previousSlaStatus = this.brandDetails?.is_age_sla_timer_enabled || false;
    this.previousTatStatus = this.brandDetails?.is_new_tat_filter_enabled || false;

    // Disable save and cancel buttons until next changes
    this.isSaveEnabled = false;
    this.isCancelling = false;

  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    const hasUnsavedChanges = this.isSaveEnabled;
    if (hasUnsavedChanges) {
      return this.confirmDiscardChanges();
    }
    return true;
  }

  /**
   * Show confirmation dialog for unsaved changes
   */
  confirmDiscardChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      if (Helpers && Helpers.SmartMessageBox) {
        Helpers.SmartMessageBox(
          {
            title: "You have reordered the rows",
            content: "To save your changes, please use the <b>Save</b> button at the top right of the page. Click <b>Discard Changes</b> to lose your edits and continue. Click <b>Cancel</b> to close this popup and keep editing.",
            buttons: [
              { text: "Cancel", type: "secondary", key: "Cancel" },
              { text: "Discard Changes", type: "primary", key: "Discard" }
            ]
          },
          (ButtonPressed: string) => {
            setTimeout(() => {
              if (ButtonPressed === "Discard") {
                this.isSaveEnabled = false;
                resolve(true);
              } else {
                resolve(false);
              }
            }, 50);
          }
        );
      } else {
        // Fallback if Helpers is not available
        const shouldDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
        if (shouldDiscard) {
          this.isSaveEnabled = false;
        }
        resolve(shouldDiscard);
      }
    });
  }
}
